import { Injectable } from '@angular/core'
import { SyncEvent, VoidSyncEvent } from 'ts-events'
import { Events } from 'ionic-angular'


import { SignalingMessageType, DomainEvents, User, Device } from '../../models/models'

import { AuthProvider } from '../auth/auth'
import { SignalingSocketIoProvider } from '../../providers/signaling/socket_io'

import { ConfigProvider } from '../config/config'
import { DeviceProvider } from '../device/device'

//import "simple-peer";

declare var SimplePeer: any

/*
Gestisce la negoziazione webrtc tramite SimplePeer + signaling.

Gestisce l'autenticazione automatica al server di signaling in base al dispositivo e allo stato di autenticazione

Si può usare in 2 modi: Ricezione o chiamata
Per ricevere:
1 Effettuare il login, viene invocato automaticamente listen()
2 Impostare onStream() per gestire lo stream in arrivo, dopo aver inizializzato le UI di ricezione
3 per rispondere con stream in uscita invocare answer() (o addStream())

Per chiamare:
1 impostare onStream per ricevere eventuale stream in arrivo, dopo aver inizializzato le UI di ricezione
2 invocare call() per avviare la chiamata
*/
@Injectable()
export class WebrtcProvider {
	private isPeerInitialized: boolean = false;
	/**
Se true, gli eventi di disconnessione del peer sono ignorati.
Usato durante il refresh della connessione
	*/
	private isPeerCloseIgnored: boolean = false;
	private isRenegotiating: boolean = false;

	private debug_timer = null;

	/**
	Chiusura connessione P2P (peer.onclose)
	*/
	public onClose: VoidSyncEvent = new VoidSyncEvent();
	/**
	Errore connessione P2P (peer.onerror)
	*/
	public onError: VoidSyncEvent = new VoidSyncEvent();
	/**
	Inizio invio stream

	da usare tramite emitOnSend(stream)
	*/
	public onSend: SyncEvent<MediaStream> = new SyncEvent<MediaStream>();
	/**
	Ricezione stream P2P (peer.onstream)
	*/
	public onStream: SyncEvent<MediaStream> = new SyncEvent<MediaStream>();

	/**
	Id del peer remoto in caso di connessione attiva
	*/
	public get otherId() {
		if (!this.signaling.isAuth) {
			return null
		}
		return this.signaling.otherId
	}

	private peer: any = null;

	/**
	Recupera l'id del peer attualmente attivo (nessuno se non connesso)
	*/
	public get peerId() {
		if (!this.signaling.isAuth) {
			return null
		}
		return this.signaling.peerId
		//return SignalingProvider.signalingServerConnection.peerId;
	}

	constructor(
		private auth: AuthProvider,
		private devices: DeviceProvider,
		private events: Events,
		public signaling: SignalingSocketIoProvider
	) {
		console.group('Nuovo gestore di chiamate webRTC')
		let self = this

		window.addEventListener('beforeunload', () => {
			console.debug('[WebrtcProvider.window.beforeUnload()] Uscita dalla app, disconnessione dal server di signaling..')
			self.disconnect()
			self.signalingLogout()
		})

		this.events.subscribe(DomainEvents.USER_LOGIN, function (_user: User, _time) {
			console.debug('[WebrtcProvider.events.on(USER_LOGIN)] Ricevuta notifica di login, reset peer e connessione al server di signaling')
			self.resetConnection()
		})
		this.events.subscribe(DomainEvents.USER_LOGOUT, function (_user: User, _time) {
			console.debug('[WebrtcProvider.events.on(USER_LOGOUT)] Ricevuta notifica di logout, rimozione peer e connessione al server di signaling')
			self.resetConnection()
		})

		this.events.subscribe(DomainEvents.DEVICE_ACTUAL_UPDATED, function (device: Device, _time: any, previous: Device) {
			if (!self.auth.isAuth && (!device || !previous || device.type != previous.type)) {
				console.debug('[WebrtcProvider.events.on(DEVICE_ACTUAL_UPDATED)] Tipo del dispositivo aggiornato per utente non autenticato, precedente connessione device non più valida, disconnessione dal server di signaling')
				self.resetConnection()
			}
		})

		console.groupEnd()
	}

	/**
	Aggiunge uno stream da inviare (usabile dal chiamato per rispondere a connessione già aperta)
	*/
	public addStream(sendVideo: boolean = false, sendAudio: boolean = true) {
		console.group('[WebrtcProvider.addStream(sendVideo=' + sendVideo + ',sendAudio=' + sendAudio + ')] Aggiunta di stream alla connessione esistente')

		if (!this.peer) {
			console.error('Impossibile aggiungere uno stream perchè il peer non è connesso')
			console.groupEnd()
			return
		}

		let self = this
		navigator.mediaDevices.getUserMedia({
			video: sendVideo,
			audio: sendAudio
		}).then(
			stream => {
				console.group('[navigator.getUserMedia().then()] Stream recuperati, li aggiungo alla connessione P2P RTC..')



				//provare a forzare i codec a VP8
				let w = <any>window
				let supportsSetCodecPreferences = w.RTCRtpTransceiver && 'setCodecPreferences' in w.RTCRtpTransceiver.prototype
				if (supportsSetCodecPreferences) {
					console.debug('[addStream()] setCodecPreferences supportato, imposto le preferenze del codec')

					let is_codec_preferences_enabled = true
					if (is_codec_preferences_enabled) {
						let transceivers = this.peer._pc.getTransceivers()
						transceivers.forEach(transceiver => {
							let kind = transceiver.sender.track.kind

							//video: riordino i codec
							if (kind === 'video') {
								let send_codecs = RTCRtpSender.getCapabilities('video').codecs
								let recv_codecs = RTCRtpReceiver.getCapabilities('video').codecs

								let sorted_codecs = []

								send_codecs.forEach(c => {
									if (c.mimeType == 'video/VP8' || c.mimeType == 'video/VP9') {
										//in testa
										sorted_codecs.unshift(c)
									} else {
										//in coda
										sorted_codecs.push(c)
									}
								})

								recv_codecs.forEach(c => {
									if (c.mimeType == 'video/VP8' || c.mimeType == 'video/VP9') {
										//in testa
										sorted_codecs.unshift(c)
									} else {
										//in coda
										sorted_codecs.push(c)
									}
								})

								console.debug('[WebrtcProvider.new()] Imposto i codec riordinati:', sorted_codecs)
								transceiver.setCodecPreferences(sorted_codecs)
							}
						})
					}
				} else {
					console.warn('setCodecPreferences non supportato')
				}


				self.peer.addStream(stream)
				//notifica invio
				self.emitOnSend(stream)
				console.groupEnd()
			}).catch(e => {
				console.error('[navigator.getUserMedia().catch()] Errore recuperando gli stream da inviare:', e)
				console.groupEnd()
			})
		/*, (stream) => {
			console.group('[navigator.getUserMedia().then()] Stream recuperati, li aggiungo alla connessione P2P RTC..')
			self.peer.addStream(stream)
			//notifica invio
			self.emitOnSend(stream)
			console.groupEnd()
		}, (e) => {
			console.error('[navigator.getUserMedia().catch()] Errore recuperando gli stream da inviare:', e)
			console.groupEnd()
		})*/
	}

	/**
	Se chiamato, risponde eventualmente inviando stream
	*/
	public answer(sendVideo = false, sendAudio = true) {
		console.debug('Invio stream di risposta dal chiamato al chiamante, video:' + sendVideo + ', audio:' + sendAudio)
		this.addStream(sendVideo, sendAudio)
	}

	/**
	Chiamata in uscita

	1 esegue il login sul signaling

	2 registra call_handled che farà connect() dopo che il peer remoto avrà aperto le UI di chiamata

	3 invia call_init

	Parametri:
	- otherId: peer id chiamato
	- video: Se inviare il video, default true
	- audio: se inviare audio, default true
	- housingUnitId: Unità abitativa per la quale parte la chiamata, default null
	*/
	public call(otherId: string, video: boolean = true, audio: boolean = true, housingUnitId: number = null): Promise<void> {
		console.group('[WebrtcProvider.call(otherId=' + otherId + ', video=' + video + ', audio=' + audio + ')] Avvio chiamata in uscita')
		let self = this

		return new Promise<void>((resolve, reject) => {
			self.signalingLogin().then(() => {
				//Questo peer è chiamante e il peer remoto ha aperto le UI: Invio stream
				self.signaling.onCallHandled.once(this, () => {
					console.log("[WebrtcProvider.signaling.onCallHandled.once()] Chiamata presa in gestione dal chiamato, connetto il peer e invio lo stream..")
					//questo verrà chiamato dopo che il peer remoto ha aperto le UI
					self.connect(video, audio, true).then(() => {
						console.log('Connessione avviata')
					}).catch(e => {
						console.error('Errore connettendo il peer per chiamare', e)
						reject(e)
					})
				})

				//(re)inizializza gli _iceServers
				self.devices.initIceServers().then(() => {
					//Invia la richiesta di chiamata
					self.signaling.sendCallInit(otherId, housingUnitId)

					resolve()
				})
				console.groupEnd()
			}).catch(e => {
				console.error('Errore registrandosi al server di signaling', e)
				console.groupEnd()
				reject(e)
			})
		})
	}

	/**
	Inizializza la connessione, eventualmente inviando degli stream appena possibile.

	L'invio è opzionale, la connessione senza invio abilita la ricezione;
	Fino a che non invoca questo metodo non può ricevere stream
	Nota: Può inviare stream solo se isCaller=false o ha già impostato otherId
	*/
	private connect(video: boolean, audio: boolean, isCaller: boolean): Promise<void> {
		let self = this
		console.debug('[WebrtcProvider.connect(video=' + video + ', audio=' + audio + ', isCaller=' + isCaller + ')] Connessione P2P RTC')

		console.debug('[WebrtcProvider.connect()] Inizializzazione connessione P2P RTC ' + (isCaller ? 'attiva' : 'passiva'))

		if (this.peer) {
			console.warn('[WebrtcProvider.connect()] Invocato connect con peer già connesso')
			//WARN Problema: Se già sconnesso scatena la catena di eventi di disconnessione e termina la chiamata che si sta eventualmente inizializzando, quindi disconnette senza avviare gli evenbti di disconnessione
			this.disconnect(false)
		}
		/* let wrtc = {
			RTCPeerConnection: window.RTCPeerConnection,
			RTCSessionDescription: window.RTCSessionDescription,
			RTCIceCandidate: window.RTCIceCandidate
		} */
		//server ice
		let peerConfig: any = {
			initiator: isCaller,

			/*aggiunte queste 3 opzioni per cercare di risolvere errore "Error: Ice connection failed"*/
			reconnectTimer: 100,
			//iceTransportPolicy: 'relay',
			trickle: true,//true: se supportato invia i candidati ICE via via che li trova invece che tutti insieme; richiede rinegoziazione

			/*
			TODO Forzare il codec video a VP8 o VP9 trasformando l'SDP
			vedi https://github.com/feross/simple-peer#peer--new-peeropts
			*/
			sdpTransform: function (sdp) {
				console.debug('[WebrtcProvider.SimplePeer.sdpTransform()] Trasformazione SDP per invio', sdp)
				return sdp
			},

			config: { iceServers: DeviceProvider.iceServers }
		}
		console.debug('[WebrtcProvider.connect()] Configurazione per la connessione P2P RTC e i server ICE:', peerConfig)

		if (!audio && !video) { //riceve senza inviare
			console.debug("[WebrtcProvider.connect()] Inizializzazione del simplepeer locale senza stream da inviare..")
			this.peer = new SimplePeer(peerConfig)

			this.initPeer() //eventi del peer e messaggi di signaling
			return Promise.resolve()
		}

		console.debug("[WebrtcProvider.connect()] Inizializzo il simplepeer locale e quindi invio lo stream..")

		//altrimenti invia stream
		return new Promise((resolve, reject) => {
			console.debug("[WebrtcProvider.connect()] Recupero gli ICE server e inizializzo il peer locale..")

			let n = <any>navigator
			// get video/voice stream
			n.getUserMedia({
				video: video,
				audio: audio
			}, (stream) => {
				//aggiunge lo stream alla configurazione, per inviarlo immediatamente
				peerConfig.stream = stream
				console.group('[WebrtcProvider.connect()] Stream locale recuperato, inizializzazione della connessione P2P RTC e invio stream')

				self.peer = new SimplePeer(peerConfig)
				self.initPeer() //eventi del peer e messaggi di signaling
				//notifica invio
				self.emitOnSend(stream)

				console.debug('[WebrtcProvider.connect()] Peer locale inizializzato.')
				console.groupEnd()

				resolve()
			}, (e) => {
				console.error('Errore in getUserMedia():', e)
				reject(e)
			})
		})
	}

	private emitOnSend(outputStream: MediaStream) {
		console.group("[WebrtcProvider.emitOnSend()] Propago l'evento di invio stream:", outputStream)
		console.debug('Tracce audio inviate:', outputStream.getAudioTracks())
		console.debug('Tracce video inviate:', outputStream.getVideoTracks())

		this.onSend.post(outputStream)
		console.groupEnd()
	}

	private emitOnStream(inputStream: MediaStream) {
		console.group("Propagazione dell'evento di ricezione stream..")
		console.debug('Tracce audio ricevute:', inputStream.getAudioTracks())
		console.debug('Tracce video ricevute:', inputStream.getVideoTracks())

		this.onStream.post(inputStream)
		console.groupEnd()
	}

	/**
	Genera l'id da usare per il peer locale
	*/
	public getPeerId(): Promise<string> {
		let self = this
		return new Promise((success, fail) => {
			if (self.auth.isAuth) {//si connette come utente
				console.debug('[WebrtcProvider.getPeerId()] Utente autenticato. Genero il peer ID in base all\'utente:', self.auth.user)
				let peerId = ConfigProvider.generatePeerIdUser(self.auth.user.id)
				return success(peerId)
			} else {//si connette come dispositivo
				console.group('[WebrtcProvider.getPeerId()] Utente non autenticato. Genero il peer ID in base al dispositivo..')
				self.devices.getIdentifier().then(identifier => {
					if (identifier) {
						console.debug('[WebrtcProvider.devices.getIdentifier()] ID dispositivo recuperato', identifier)
						let peerId = ConfigProvider.generatePeerIdDevice(identifier)
						console.debug('[WebrtcProvider.devices.getIdentifier()] Peer ID generato da dispositivo', peerId)
						success(peerId)
						console.groupEnd()
						return
					}
					console.error('[WebrtcProvider.devices.getIdentifier()] ID dispositivo generato nullo')
					console.groupEnd()
					fail('Impossibile recuperare un idantificativo univoco per il device')
				}).catch(error => {
					console.error('[WebrtcProvider.devices.getIdentifier()] Errore recuperando l\'ID dispositivo')
					console.groupEnd()
					fail(error)
				})
			}
		})
	}

	public codec_log() {
		let pc = this.peer._pc
		if (!pc) {
			console.info('[codec_log()] SimplePeer._pc null!')
			return
		}

		console.group('[codec_log()] Verifica protocollo in uso webrtc')

		pc.getStats(null).then((stats: any[]) => {
			let stats_logged = []
			stats.forEach(stat => {
				if (stat.type == 'codec') {
					stats_logged.push(stat)
				}
			})
			console.debug('[codec_log()] SimplePeer._pc stats:', stats_logged)
			console.groupEnd()
		}, e => {
			console.error('Errore recuperando le statistiche della connessione', e)
			console.groupEnd()
		})
	}

	public codec_log_start() {
		console.debug('[codec_log_start()] Avvio log codec')

		if (this.debug_timer) {
			clearInterval(this.debug_timer)
		}
		let self = this
		this.debug_timer = setInterval(() => {
			if (!self.peer) {
				clearInterval(this.debug_timer)
			} else {
				self.codec_log()
			}
		}, 60000)

		self.codec_log()
	}

	public codec_log_stop() {
		console.debug('[codec_log_start()] Stop log codec')

		if (this.debug_timer) {
			clearInterval(this.debug_timer)
		}
	}

	/**
	Termina l'eventuale chiamata aperta o in attesa

	Deregistra il listener dei messaggi di signaling;
	Disconnette e dealloca il peer;
	*/
	public disconnect(emitPeerClosed: boolean = true) {
		console.debug('[WebrtcProvider.disconnect()] Chiusura chiamata tramite rimozione dei listener signaling e finalizzazione del peer')
		//smette di seguire il signaling
		this.signaling.detach(this)

		if (this.peer) {
			//nel caso inibisce la propagazione dell'evento di chiusura
			this.isPeerCloseIgnored = !emitPeerClosed
			this.peer.destroy()//chiude e deregistra i listener
			this.peer = null
			this.isPeerInitialized = false
			this.isPeerCloseIgnored = false
		}
	}

	/*
	REGISTRA I GESTORI DI EVENTI SUL PEER E DOPO I MESSAGGI IN ARRIVO DA GESTIRE COL PEER
	*/
	private initPeer() {
		console.group('Inizializzazione del peer e del canale dei messaggi in arrivo..')
		let self = this
		if (self.isPeerInitialized) {
			console.warn('Peer già connesso e inizializzato, non reinizializzo')
			console.groupEnd()
			return
		}
		self.isPeerInitialized = true

		self.peer.on('connect', () => {
			console.debug("[WebrtcProvider.peer.on('connect')] Connessione P2P RTC inizializzata.")
			//self.codec_log_start()
		})

		//signal pronto per invio
		self.peer.on('signal', (data) => {
			console.group("[WebrtcProvider.peer.on('signal')] Messaggio di signaling RTC emesso dal peer locale:", data)
			if (data) {
				if (data.renegotiate) {
					if (self.isRenegotiating == data.renegotiate) {
						console.warn("Rinegoziazione già in atto, ignoro la richiesta")
						return
					}
					self.isRenegotiating = data.renegotiate
					console.log("Invio RENEGOTIATE:", data.renegotiate)
				} else {
					self.isRenegotiating = false
				}
			}

			let message = {
				type: SignalingMessageType.SIGNALING,
				data: data
			}

			//il canale di invio deve essere già pronto quando si inizializza il peer
			self.signaling.send(message)
			console.groupEnd()
		})

		//ricezione stream
		self.peer.on('stream', (stream: MediaStream) => { //stream in arrivo
			console.debug("[WebrtcProvider.peer.on('stream')] Stream P2P RTC in arrivo:", stream)

			self.emitOnStream(stream)

			//self.codec_log_start()
		})

		self.peer.on('close', () => {
			console.debug("[WebrtcProvider.peer.on('close')] Connessione P2P RTC chiusa.")

			if (self.isPeerCloseIgnored) {
				console.debug("[WebrtcProvider.peer.on('close')] Disconnessione ignorata.")
				return
			}
			self.onClose.post()

			self.codec_log_stop()
		})

		self.peer.on('error', (error) => {
			console.error("[WebrtcProvider.peer.on('error')] Errore P2P RTC, codice errore " + error.code + ":", error)
			/*
			error codes
			Errors returned by the error event have an err.code property that will indicate the origin of the failure.

			Possible error codes:
			ERR_WEBRTC_SUPPORT
			ERR_CREATE_OFFER
			ERR_CREATE_ANSWER
			ERR_SET_LOCAL_DESCRIPTION
			ERR_SET_REMOTE_DESCRIPTION
			ERR_ADD_ICE_CANDIDATE
			ERR_ICE_CONNECTION_FAILURE
			ERR_SIGNALING
			ERR_DATA_CHANNEL
			*/
			self.onError.post()
		})

		//Nota: Il peer deve essere già inizializzato quando si ricevono i messaggi
		self.signaling.onSignaling.attach(this, (messageData) => {
			console.debug('[WebrtcProvider.signaling.onSignaling(messageData)] Messaggio di signaling ricevuto, forwarding al peer:', messageData)

			if (!self.peer) return

			const pc = self.peer._pc
			// Se arriva un answer quando siamo già STABLE => è quasi certamente duplicato o vecchio
			if ((messageData ? messageData.type : null) === 'answer' && ((pc ? pc.signalingState : null) === 'stable')) {
				console.warn('[WebrtcProvider] Answer ricevuta DOPO aver raggiunto lo stato stable (duplicato o residuo di vecchio handshake)', {
					signalingState: pc ? pc.signalingState : null,
					local: (pc && pc.localDescription) ? pc.localDescription.type : null,
					remote: (pc && pc.remoteDescription) ? pc.remoteDescription.type : null,
				})
				return
			}

			self.peer.signal(messageData)
		})

		console.groupEnd()
	}

	/**
	Si connette in modalità passiva, accettando comunicazioni e chiamate in arrivo:

	1. Recupera o genera il peer id
	2. Inizializza il peer
	3. Si registra al server di signaling (signalingLogin) mettendosi in ascolto per messaggi di signaling al peer
	*/
	public listen(): Promise<void> {
		console.group('[WebrtcProvider.listen()] Inizializzazione P2P in ascolto accettando comunicazioni in arrivo')
		if (this.peer) {
			console.info('Peer già connesso')
			return Promise.resolve()
		}
		let self = this
		return new Promise((resolve, reject) => {
			//1 Prepara il peer in modalità ricezione, senza signaling
			self.connect(false, false, false).then(() => {
				//2 inizia a ricevere messaggi di signaling
				self.signalingLogin().then(() => {
					console.debug('Peer locale istanziato e in ascolto')
					resolve()
					console.groupEnd()
				}).catch(error => {
					console.error('Errore connettendosi al server di signaling', error)
					reject('Errore connettendosi al server di signaling')
					console.groupEnd()
				})
			})
		})
	}


	/**
	Si disconnette e riconnette in ascolto al server di signaling
	*/
	private resetConnection() {
		console.group('[WebrtcProvider.resetConnection()] Reset connessione al server di signaling..')
		this.disconnect()
		this.signalingLogout()
		this.listen().then(_ => {
			console.debug('[WebrtcProvider.resetConnection()] Connessione al server di signaling reinizializzata')
			console.groupEnd()
		}).catch(e => {
			console.error('Errore riconnettendosi al server di signaling', e)
			console.groupEnd()
		})
	}

	/**
	Login: Si assicura che sia registrato al server di signaling con un peer id, opzionalmente specificato o autogenerato, ricevendo i messaggi di signaling destinati al peer id stesso

	Uso:
	- Il chiamate deve eseguire signaling login PRIMA di inizializzare il peer, poichè in modalità attiva questo invia messaggi immediatamente.
	- Il chiamato esegue sgnaling login DOPO aver inizializzato il peer, poichè in modalità passiva si attiva solo a seguito del signaling.
	*/
	private signalingLogin(peerId = null): Promise<void> {
		let self = this

		if (peerId === null) {
			console.group('[WebrtcProvider.signalingLogin(null)] Registrazione al server di signaling con peer ID non specificato, generazione del peer id prima di procedere..')
			//peer ID automatico, lo genera ed effettua una ricorsione valorizzato
			return new Promise((resolve, reject) => {
				self.getPeerId().then(peerId => {
					if (peerId) {//ok, login ricorsivo valorizzando
						console.debug('Peer id generato: ' + peerId + ', chiamata ricorsiva con il peer ID valorizzato')
						self.signalingLogin(peerId).then(() => {
							console.debug('Registrato al server di signaling.')
							console.groupEnd()
							resolve()
						}).catch((e) => {
							console.error('Errore generando il peer ID', e)
							console.groupEnd()
							reject(e)
						})
					} else {
						console.error('Tentativo d generare il peer id fallito')
						console.groupEnd()
						reject('Impossibile generare il peer ID locale')
					}
				}).catch(e => {
					console.error('Errore generando il peer ID locale', e)
					console.groupEnd()
					reject('Errore generando il peer ID locale')
				})
			})
		}

		console.debug('[WebrtcProvider.signalingLogin(' + peerId + ')] Registrazione al server di signaling con peer ID: ' + peerId)

		if (this.signaling.peerId == peerId) {
			console.debug('Già registrato con questo peer ID.')
			return Promise.resolve()
		}

		return this.signaling.login(peerId).then(() => {
			console.debug('Registrato al server di signaling.')

			//gestione messaggio di hangup
			self.signaling.onCallHangup.once(self, (_peerId) => {
				console.debug('Hangup dal peer remoto, disconnetto questo peer')
				self.disconnect()
			})
		}).catch(e => {
			console.error('Impossibile registrarsi al server di signaling', e)
			return Promise.reject('Impossibile registrarsi al server di signaling: ' + e)
		})
	}

	/**
	Logout: Si disconnette dal server di signaling
	*/
	private signalingLogout() {
		console.debug('[WebrtcProvider.signalingLogout()] Deregistrazione dal server di signaling')
		this.signaling.logout()
	}
}



// WEBPACK FOOTER //
// ./src/providers/webrtc/webrtc.ts